<template>
  <div class="bg-white rounded-lg p-6 border-[1px] border-gray-200">
    <div class="flex items-center">
      <div>
        <h3 class="font-medium w-64">Custom week access</h3>
        <p class="text-xs text-gray-500">
          Global default setting: {{ weekPreference?.default_preview_weeks }} weeks
        </p>
      </div>
      <div>
        <div v-if="isLoadingPreference" class="flex items-center justify-center py-2">
          <Loader2 class="h-4 w-4 animate-spin text-gray-500" />
        </div>
        <div v-else-if="!weekPreference?.custom_preview_weeks && !isEditing" class="flex items-center gap-2">
          <span class="text-gray-500 mr-4">None</span>
          <Button
            @click="startEditing"
            class="bg-cosmicPurple shadow-none text-white hover:bg-cosmicPurple/80 hover:-translate-y-[1px] transition-all duration-200 font-normal"
          >
            Add Custom Value
          </Button>
        </div>
        <div v-else class="flex items-center gap-2">
          <input
            type="number"
            :value="weekPreference?.custom_preview_weeks ?? weekPreference?.default_preview_weeks"
            @input="(e) => weekPreference && (weekPreference.custom_preview_weeks = +(e.target as HTMLInputElement).value)"
            min="1"
            :disabled="isLoadingPreference"
            class="w-32 p-2 border border-gray-300 rounded-md"
          />
          
          <div class="flex items-center gap-2">
            <Button
              v-if="isEditing"
              :disabled="isLoadingPreference || isSavingPreference || !hasValueChanged"
              @click="saveWeekPreference"
              class="bg-cosmicPurple shadow-none text-white hover:bg-cosmicPurple/80 hover:-translate-y-[1px] transition-all duration-200 font-normal"
            >
              <Loader2 v-if="isSavingPreference" class="mr-2 h-4 w-4 animate-spin" />
              Save
            </Button>
            <template v-else>
              <Button
                :disabled="isLoadingPreference || isSavingPreference || !hasValueChanged"
                @click="saveWeekPreference"
                class="bg-cosmicPurple shadow-none text-white hover:bg-cosmicPurple/80 hover:-translate-y-[1px] transition-all duration-200 font-normal"
              >
                <Loader2 v-if="isSavingPreference" class="mr-2 h-4 w-4 animate-spin" />
                Save
              </Button>
              <Button
                :disabled="isLoadingPreference || isDeletingPreference"
                @click="deleteCustomPreference"
                class="shadow-none text-gray-500 border-gray-300 border-[1px] bg-white hover:bg-gray-100 hover:-translate-y-[1px] transition-all duration-200 font-normal"
              >
                <Loader2 v-if="isDeletingPreference" class="mr-2 h-4 w-4 animate-spin" />
                Remove
              </Button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import { API_ROUTES } from '@/const/apiRoutes'
import { useToast } from 'primevue/usetoast'
import api from '@/utils/api'

interface WeekPreference {
  custom_preview_weeks: number | null
  default_preview_weeks: number
}

const props = defineProps<{
  userId: number
}>()

const toast = useToast()

const weekPreference = ref<WeekPreference | null>(null)
const originalValue = ref<number | null>(null)
const isLoadingPreference = ref(false)
const isSavingPreference = ref(false)
const isDeletingPreference = ref(false)
const isEditing = ref(false)

const hasValueChanged = computed(() => {
  if (!weekPreference.value) return false
  return weekPreference.value.custom_preview_weeks !== originalValue.value
})

const startEditing = () => {
  isEditing.value = true
  if (weekPreference.value) {
    weekPreference.value.custom_preview_weeks = weekPreference.value.default_preview_weeks
    originalValue.value = null
  }
}

const fetchWeekPreference = async () => {
  if (!props.userId) return
  
  isLoadingPreference.value = true
  try {
    const response = await api.get(API_ROUTES.GET_USER_WEEK_PREFERENCE(props.userId))
    console.log('API Response:', response.data)
    weekPreference.value = {
      custom_preview_weeks: response.data.custom_preview_weeks,
      default_preview_weeks: response.data.default_preview_weeks
    }
    originalValue.value = response.data.custom_preview_weeks
  } catch (err: any) {
    console.error('Failed to fetch week preference:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.response?.data?.message || 'Failed to fetch week preference',
      life: 3000
    })
  } finally {
    isLoadingPreference.value = false
  }
}

const saveWeekPreference = async () => {
  if (!weekPreference.value || isSavingPreference.value || !hasValueChanged.value) return
  
  isSavingPreference.value = true
  try {
    await api.put(API_ROUTES.SET_USER_WEEK_PREFERENCE(props.userId), {
      preview_weeks: weekPreference.value.custom_preview_weeks
    })
    isEditing.value = false
    originalValue.value = weekPreference.value.custom_preview_weeks
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Week preference updated successfully',
      life: 3000
    })
  } catch (err: any) {
    console.error('Failed to update week preference:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.response?.data?.message || 'Failed to update week preference',
      life: 3000
    })
  } finally {
    isSavingPreference.value = false
  }
}

const deleteCustomPreference = async () => {
  if (!weekPreference.value || isDeletingPreference.value) return
  
  isDeletingPreference.value = true
  try {
    await api.delete(API_ROUTES.DELETE_USER_WEEK_PREFERENCE(props.userId))
    weekPreference.value.custom_preview_weeks = null
    originalValue.value = null
    isEditing.value = false
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Custom week preference removed',
      life: 3000
    })
  } catch (err: any) {
    console.error('Failed to delete custom preference:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.response?.data?.message || 'Failed to delete custom preference',
      life: 3000
    })
  } finally {
    isDeletingPreference.value = false
  }
}

// Fetch data on mount
// eslint-disable-next-line @typescript-eslint/no-floating-promises
fetchWeekPreference()
</script> 